import { getDefaultLoggingSettings, LoggingSection } from "@multiweb/logging";
import { createDefaultSectionLoggerFactory } from "Logging/defaultLogger";
import { IndexedDBPromise } from "../../IndexedDB/Promises/IndexedDBPromise";
import { IIndexedDbManager } from "./IIndexedDbManager";

/**
 * Logger pro `IndexedDbManager`. Logger se používá ještě před tím, než je k dispozici nastavení logování. (V `ServiceWorkerContext._getConfiguration()`.)
 * Proto si musíme vystačit s výchozím loggerem i nastavením.
 */
const logger = createDefaultSectionLoggerFactory(getDefaultLoggingSettings(false)).create(LoggingSection.indexedDB);

export class IndexedDbManager implements IIndexedDbManager {
    protected __storageDb?: Promise<IDBDatabase>;
    protected readonly _databaseName: string;
    protected readonly _databaseVersion: number;
    protected readonly _storeNames: string[] = [];

    protected get _storageDb(): Promise<IDBDatabase> {
        if (!this.__storageDb) {
            this.__storageDb = IndexedDBPromise.createOrOpenDB(this._databaseName, db => this._onCreateDb(db), this._databaseVersion);
        }
        return this.__storageDb;
    }

    constructor(databaseName: string, databaseVersion: number) {
        this._databaseVersion = databaseVersion;
        this._databaseName = databaseName;
    }

    public registerStore(storageName: string): void {
        if (!this._storeNames.includes(storageName))
            this._storeNames.push(storageName);
    }

    /**
     * Vytváření struktur v indexeddb po jejím vytvoření
     * @param db db předaná při události
     */
    protected _onCreateDb(db: IDBDatabase): void {
        if (this._storeNames.length === 0)
            throw new Error("Unable to create IndexedDB schema. No store names available.");

        const existingStoreNames = Array.from(db.objectStoreNames);
        for (const existingStoreName of existingStoreNames)
            if (this._storeNames.includes(existingStoreName))
                db.deleteObjectStore(existingStoreName);

        this._storeNames.forEach(storeName => db.createObjectStore(storeName, { keyPath: this._getKeyPath() }));
    }

    /**
     * Vrátí klíče k IndexedDB
     */
    protected _getKeyPath(): string | string[] {
        return "_name";
    }

    public async getValue(storeName: string, name: string): Promise<any> {
        try {
            const db = await this._storageDb;
            const dbValue = await IndexedDBPromise.getRowByIndexValue(db, storeName, name);
            if (dbValue)
                return dbValue.value;
            return undefined;
        } catch (e) {
            logger.error(`Failed getting value '${name}' from ${this._storeNames} in IndexedDB`); //`
            return undefined;
        }
    }

    public async getAllValues(storeName: string): Promise<any[]> {
        const db = await this._storageDb;
        return IndexedDBPromise.getAllRows(db, storeName);
    }

    public async setValue(storeName: string, name: string, value: any): Promise<void> {
        try {
            const db = await this._storageDb;
            await IndexedDBPromise.updateRow(db, storeName, name, undefined, { _name: name, value: value });
        } catch (e) {
            logger.error(`Failed setting value '${name}' to ${storeName} in IndexedDB`); //`
        }
    }

    public async clearAllValues(): Promise<void> {
        const db = await this._storageDb;
        const osNames = Array.from(db.objectStoreNames);
        for (const os of osNames) {
            if (this._storeNames.includes(os)) {
                await IndexedDBPromise.clearStore(db, os);
            }
        }
    }

    public async clearAllStoreValues(storeName: string): Promise<void> {
        const db = await this._storageDb;
        if (this._storeNames.includes(storeName)) {
            await IndexedDBPromise.clearStore(db, storeName);
        }
    }

    public async deleteDb(): Promise<void> {
        await IndexedDBPromise.deleteDB(this._databaseName);
    }
}
